import { MultiselectAutosuggest } from '@seek/multiselect-autosuggest';
import { useTranslations } from '@vocab/react';
import {
  Bleed,
  Box,
  ButtonIcon,
  Column,
  Columns,
  IconChevron,
  IconClear,
  IconLocation,
  Loader,
} from 'braid-design-system';
import { type ComponentProps, useMemo, useState, type FC } from 'react';

import { useAppConfig } from 'src/constants/AppConfig/AppConfigProvider';
import { XSmallCheckbox } from 'src/libs/XSmallCheckbox';

import { ResponsiveText } from '../ResponsiveText';

import translations from './.vocab';
import { fuzzyFilter } from './fuzzyFilter';
import { groupLocationsByMarketOrderAndMapCount } from './groupLocationsByMarketOrderAndMapCount';

import * as styles from './styles.css';

interface TrackingChange {
  type: 'Selected' | 'Deselected';
  item: LocationFilterSuggestionItem;
}

interface LocationFilterProps {
  loading: boolean;
  locations: HomeLocationFacet[];
  jobPostingCountry: string;
  selectedHomeLocations: LocationFilterSuggestionItem[];
  setSelectedHomeLocations: (props: {
    homeLocations: LocationFilterSuggestionItem[];
    changes: TrackingChange[];
  }) => void;
  hideLabel: boolean;
  dropdownBoxProps: ComponentProps<typeof Box>;
}

export interface HomeLocationFacet {
  homeLocation: {
    id: string;
    description: string;
    displayDescription: string;
    isSeekMarketCountry: boolean;
    country: string;
  };
  children?: HomeLocationFacet[];
  count: number;
}
export interface GroupedSuggestionItem {
  label: string;
  suggestions: SelectableSuggestionItem[];
}
export interface SelectableSuggestionItem {
  type: 'selectable-suggestion';
  group: string;
  id: string;
  name: string;
  count: number;
  indentLevel: number;
}

export type LocationFilterSuggestionItem =
  | {
      type: 'group-heading';
      id: string;
      name: string;
    }
  | SelectableSuggestionItem;

const id = 'location-filter';

export const LocationFilter: FC<LocationFilterProps> = ({
  loading,
  locations,
  jobPostingCountry,
  selectedHomeLocations,
  dropdownBoxProps,
  hideLabel,
  setSelectedHomeLocations,
}) => {
  const { t } = useTranslations(translations);

  const { language } = useAppConfig();

  const [searchText, setSearchText] = useState('');

  const [groupExpandedStateMap, setGroupExpandedStateMap] = useState<
    Record<string, boolean>
  >({});

  const { locationCountMap, unfilteredGroupedLocations } = useMemo(
    () =>
      groupLocationsByMarketOrderAndMapCount({
        jobPostingCountry,
        locations,
        language,
        restOfWorldLabel: t('Rest of world'),
      }),
    [jobPostingCountry, locations, language, t],
  );

  const groupedLocationsToUse = useMemo(() => {
    if (searchText) {
      const filteredGroupedLocations = unfilteredGroupedLocations
        .flatMap(({ label, suggestions }) => ({
          label,
          ...fuzzyFilter({
            items: suggestions.map((suggestion) => ({
              ...suggestion,
              indentLevel: 0,
            })),
            searchText,
          }),
        }))
        .filter(({ suggestions }) => suggestions.length > 0)
        .sort((a, b) => a.maxScore - b.maxScore)
        .map(({ label, suggestions }) => ({
          label,
          suggestions,
        }));

      setGroupExpandedStateMap(
        filteredGroupedLocations.reduce(
          (acc, { label }) => ({ ...acc, [label]: true }),
          {},
        ),
      );
      return filteredGroupedLocations;
    }

    setGroupExpandedStateMap((prevMap) =>
      unfilteredGroupedLocations.reduce(
        (acc, { label }) => ({
          ...acc,
          [label]: prevMap[label] ?? unfilteredGroupedLocations.length < 2,
        }),
        {},
      ),
    );

    return unfilteredGroupedLocations;
  }, [searchText, unfilteredGroupedLocations]);

  const toggleGroupExpandedState = (groupLabel: string) => {
    setGroupExpandedStateMap((prevMap) => ({
      ...prevMap,
      [groupLabel]: !prevMap[groupLabel],
    }));
  };

  const downshiftItems: LocationFilterSuggestionItem[] =
    groupedLocationsToUse.flatMap((item) => [
      {
        type: 'group-heading',
        id: item.label,
        name: item.label,
      },
      ...item.suggestions,
    ]);

  const highlightedIndexMap = downshiftItems.reduce<
    Record<`${LocationFilterSuggestionItem['type']}-${string}`, number>
  >(
    (acc, suggestion, index) => ({
      ...acc,
      [`${suggestion.type}-${suggestion.id}`]: index,
    }),
    {},
  );

  const isSelected = (itemId: string) =>
    selectedHomeLocations.some((selectedItem) => selectedItem.id === itemId);
  const removeItem = (item: SelectableSuggestionItem) => {
    const newSelectedItems = [
      ...selectedHomeLocations.filter(
        (selectedItem) => selectedItem.id !== item.id,
      ),
    ];
    setSelectedHomeLocations({
      homeLocations: newSelectedItems,
      changes: [{ item, type: 'Deselected' }],
    });
  };
  const toggleItemSelectedState = (item: SelectableSuggestionItem) => {
    if (isSelected(item.id)) {
      removeItem(item);
    } else {
      setSelectedHomeLocations({
        homeLocations: [...selectedHomeLocations, item],
        changes: [{ item, type: 'Selected' }],
      });
    }
    if (searchText) {
      setGroupExpandedStateMap(
        unfilteredGroupedLocations.reduce(
          (acc, { label }) => ({
            ...acc,
            [label]: unfilteredGroupedLocations.length < 2,
          }),
          {},
        ),
      );
      setSearchText('');
    }
  };

  const baseBoxProps = {
    background: 'surface',
    borderRadius: 'standard',
    width: 'full',
  } as const;

  const iconLocation = (
    <ResponsiveText
      size={{
        mobile: 'small',
        tabletAndDesktop: 'standard',
      }}
      baseline={false}
      tone={
        selectedHomeLocations.length > 0 || searchText ? 'neutral' : 'secondary'
      }
    >
      <IconLocation />
    </ResponsiveText>
  );

  if (loading) {
    return (
      <MultiselectAutosuggest
        id={id}
        label={t('Location')}
        size="small"
        placeholder={t('Search applications by location')}
        suggestions={[]}
        selectedItems={[]}
        searchText=""
        setSearchText={() => {}}
        onItemsSelected={() => iconLocation}
        hideLabel={hideLabel}
        dropdownBoxProps={{ ...baseBoxProps, ...dropdownBoxProps }}
        renderSelectedItems={() => iconLocation}
        renderSuggestions={() => (
          <Box width="full" padding="small">
            <Loader />
          </Box>
        )}
        onClickSuggestionItem={() => {}}
      />
    );
  }

  return (
    <MultiselectAutosuggest
      id={id}
      label={t('Location')}
      size="small"
      hideLabel={hideLabel}
      dropdownBoxProps={{
        ...baseBoxProps,
        ...dropdownBoxProps,
      }}
      placeholder={t('Search applications by location')}
      suggestions={downshiftItems}
      selectedItems={selectedHomeLocations}
      searchText={searchText}
      setSearchText={(newInputValue) => {
        setSearchText((prevSearchText) => {
          if (prevSearchText && !newInputValue) {
            setGroupExpandedStateMap(() =>
              unfilteredGroupedLocations.reduce(
                (acc, { label }) => ({
                  ...acc,
                  [label]: unfilteredGroupedLocations.length < 2,
                }),
                {},
              ),
            );
          }
          return newInputValue;
        });
      }}
      onItemsSelected={(items) => {
        const changes: TrackingChange[] = [];
        items.forEach((item) => {
          if (
            !selectedHomeLocations.find(
              (selectedLocation) => selectedLocation.id === item.id,
            )
          ) {
            changes.push({
              item,
              type: 'Selected',
            });
          }
        });
        selectedHomeLocations.forEach((selectedHomeLocation) => {
          if (!items.find((item) => selectedHomeLocation.id === item.id)) {
            changes.push({
              item: selectedHomeLocation,
              type: 'Deselected',
            });
          }
        });
        setSelectedHomeLocations({
          homeLocations: items,
          changes,
        });
      }}
      renderSelectedItems={({ getSelectedItemProps }) => (
        <Box component="ul" className={styles.tagList}>
          {iconLocation}
          {selectedHomeLocations.map((item, index) => {
            if (item.type === 'selectable-suggestion') {
              const count = locationCountMap[item.id] ?? 0;
              const locationName = item.name;
              return (
                <Box
                  key={`selected-item-${item.id}`}
                  className={styles.tag}
                  borderRadius="full"
                  component="li"
                  aria-label={t('locationName count applications', {
                    count,
                    locationName,
                  })}
                  {...getSelectedItemProps({
                    selectedItem: item,
                    index,
                  })}
                >
                  <Box className={styles.tagText}>{locationName}</Box>

                  <Box className={styles.tagBadge} aria-hidden>
                    <ResponsiveText
                      size={{
                        mobile: 'small',
                        tabletAndDesktop: 'xsmall',
                      }}
                      maxLines={1}
                    >
                      {t('count', { count })}
                    </ResponsiveText>
                  </Box>

                  <ButtonIcon
                    id={`remove-${locationName}-btn`}
                    size="small"
                    icon={<IconClear />}
                    label={t('Clear {item}', { item: locationName })}
                    onClick={() => removeItem(item)}
                    variant="transparent"
                  />
                </Box>
              );
            }
          })}
        </Box>
      )}
      renderSuggestions={({ highlightedIndex, getItemProps }) =>
        groupedLocationsToUse.length > 0 ? (
          <>
            {groupedLocationsToUse.map(
              ({ label: groupLabel, suggestions: groupSuggestions }) => {
                const regionId = `region-${groupLabel}`;
                const regionLabelId = `region-label-${groupLabel}`;
                const isGroupOpen = groupExpandedStateMap[groupLabel];
                const labelIndex =
                  highlightedIndexMap[`group-heading-${groupLabel}`];
                return (
                  <Box
                    id={regionId}
                    key={groupLabel}
                    component="section"
                    role="region"
                    aria-expanded={isGroupOpen}
                    aria-labelledby={regionLabelId}
                  >
                    <Box
                      display="block"
                      cursor="pointer"
                      aria-controls={regionId}
                      padding="small"
                      className={{
                        [styles.highlighted]: highlightedIndex === labelIndex,
                      }}
                      {...getItemProps({
                        item: {
                          type: 'group-heading',
                          id: groupLabel,
                          name: groupLabel,
                        },
                        index: labelIndex,
                        role: 'button',
                        'aria-expanded': isGroupOpen,
                      })}
                    >
                      <Columns space="small">
                        <Column>
                          <ResponsiveText
                            id={regionLabelId}
                            size={{
                              mobile: 'standard',
                              tabletAndDesktop: 'small',
                            }}
                            weight="medium"
                            tone="link"
                          >
                            {groupLabel.toUpperCase()}
                          </ResponsiveText>
                        </Column>
                        <Column width="content">
                          <ResponsiveText
                            tone="link"
                            size={{
                              mobile: 'standard',
                              tabletAndDesktop: 'small',
                            }}
                          >
                            <IconChevron
                              direction={isGroupOpen ? 'up' : 'down'}
                            />
                          </ResponsiveText>
                        </Column>
                      </Columns>
                    </Box>
                    {groupSuggestions.map((suggestion) => {
                      const itemIndex =
                        highlightedIndexMap[
                          `selectable-suggestion-${suggestion.id}`
                        ];
                      const isHighlighted = highlightedIndex === itemIndex;

                      return (
                        <Box
                          key={`suggestion-item-${suggestion.id}`}
                          display={!isGroupOpen ? 'none' : undefined}
                          component="li"
                          className={{
                            [styles.listItem]: true,
                            [styles.highlighted]: isHighlighted,
                          }}
                          style={{
                            // 12px is padding size small to align with group heading
                            paddingLeft: `${
                              12 + 16 * suggestion.indentLevel
                            }px`,
                          }}
                          {...getItemProps({
                            item: { ...suggestion, id: String(itemIndex) },
                            name: suggestion.name,
                            index: itemIndex,
                          })}
                        >
                          <Columns space="small" alignY="top">
                            <Column>
                              <XSmallCheckbox
                                id={`suggestion-checkbox-${suggestion.id}`}
                                label={suggestion.name}
                                checked={isSelected(suggestion.id)}
                                // We are relying on the checkbox to propagate the click event to the parent Box with getItemProps so that the menu remains open after selection
                                onChange={() => {}}
                                // This stops the label from focusing on the checkbox which closes the dropdown
                                onClick={(e) => {
                                  e.preventDefault();
                                }}
                                ariaLabel={t(
                                  'locationName count applications',
                                  {
                                    locationName: suggestion.name,
                                    count: suggestion.count,
                                  },
                                )}
                              />
                            </Column>
                            <Column width="content">
                              <Bleed vertical="xxsmall">
                                <Box
                                  className={styles.listItemBadge}
                                  background={
                                    isHighlighted ? 'surface' : 'neutralLight'
                                  }
                                  aria-hidden
                                >
                                  <ResponsiveText
                                    size={{
                                      mobile: 'small',
                                      tabletAndDesktop: 'xsmall',
                                    }}
                                    maxLines={1}
                                  >
                                    {t('count', {
                                      count: suggestion.count,
                                    })}
                                  </ResponsiveText>
                                </Box>
                              </Bleed>
                            </Column>
                          </Columns>
                        </Box>
                      );
                    })}
                  </Box>
                );
              },
            )}
          </>
        ) : (
          <Box paddingX="small" paddingY="small" background="surface">
            <ResponsiveText
              size={{
                mobile: 'standard',
                tabletAndDesktop: 'small',
              }}
            >
              {t('No locations found')}
            </ResponsiveText>
          </Box>
        )
      }
      isItemDisabled={(item) => {
        if (!item) {
          return true;
        }

        return item.type === 'group-heading'
          ? false
          : !groupExpandedStateMap[item.group];
      }}
      onMenuClose={() => {
        setGroupExpandedStateMap(() =>
          groupedLocationsToUse.reduce(
            (acc, { label }) => ({
              ...acc,
              [label]: groupedLocationsToUse.length < 2 || searchText,
            }),
            {},
          ),
        );
      }}
      onClickSuggestionItem={(item) => {
        if (item.type === 'group-heading') {
          toggleGroupExpandedState(item.name);
        } else {
          toggleItemSelectedState(item);
        }
      }}
    />
  );
};
